import { useEffect, useState } from 'react';


const ON_MODULE_SCRIPT_EXECUTED_EVENT_NAME: string = 'module_script_executed';

const appendScriptElementToDocumentHead = (scriptId: string, scriptSrc: string, isTypeModule: boolean): HTMLScriptElement => {
  const script: HTMLScriptElement = document.createElement('script');
  script.id = scriptId;

  if (isTypeModule) {
    script.setAttribute('type', 'module');
    script.innerHTML = `
      await import('${ scriptSrc }'); // wait for the script to be executed
      document.dispatchEvent(new Event('${ ON_MODULE_SCRIPT_EXECUTED_EVENT_NAME }'));
    `;
  } else {
    script.src = scriptSrc;
  }

  document.head.appendChild(script);

  return script;
};

const getOrAppendScriptElementToDocumentHead = (scriptId: string, scriptSrc: string, isTypeModule: boolean): HTMLScriptElement => {
  let script: HTMLScriptElement | null = document.getElementById(scriptId) as HTMLScriptElement | null;

  if (!script) {
    return appendScriptElementToDocumentHead(scriptId, scriptSrc, isTypeModule);
  }

  return script;
};


interface UseCdnScriptArg<ScriptApi> {
  isTypeModule?: boolean;
  scriptId: string;
  scriptSrc: string | undefined;
  isScriptApiAvailable: () => boolean;
  parseScriptApi: () => ScriptApi | null;
}

const useCdnScript = <ScriptApi>({
                                   isTypeModule,
                                   scriptId,
                                   scriptSrc,
                                   isScriptApiAvailable,
                                   parseScriptApi,
                                 }: UseCdnScriptArg<ScriptApi>): ScriptApi | null => {
  const isApiAvailableValueInitially: boolean = isScriptApiAvailable();
  const scriptApiInitial: ScriptApi | null = isApiAvailableValueInitially ? parseScriptApi() : null;

  const [scriptApi, setScriptApi] = useState(scriptApiInitial);

  const parseAndSetScriptApi = (): void => {
    const scriptApi: ScriptApi | null = parseScriptApi();

    if (scriptApi) {
      setScriptApi(scriptApi);
    } else {
      console.error('Script could not be parsed', scriptSrc);
    }
  };

  useEffect(() => {
    if (!scriptSrc || isApiAvailableValueInitially || isScriptApiAvailable()) {
      return;
    }

    // 1. Load the script
    const abortController = new AbortController();
    const script: HTMLScriptElement = getOrAppendScriptElementToDocumentHead(scriptId, scriptSrc, isTypeModule!);

    // 2. Parse the API
    if (isTypeModule) {
      document.addEventListener(ON_MODULE_SCRIPT_EXECUTED_EVENT_NAME, parseAndSetScriptApi, abortController);
    } else {
      script!.addEventListener('load', parseAndSetScriptApi, abortController);
    }

    return () => {
      abortController.abort();
    };
  }, [scriptSrc]);

  return scriptApi;
};

export default useCdnScript;